<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import unescape from 'lodash/unescape';
    import { onDestroy } from 'svelte';

    import { NORMALIZE_CSS } from '../constants';

    export let provider = '';
    export let head = '';
    export let body = '';

    let iframeContainer: HTMLIFrameElement;
    let rsObserver: ResizeObserver | undefined;
    let msObserver: MutationObserver | undefined;

    // Prevents the iframe's height from feeding back into the embed's layout
    // (embeds using % / vh would otherwise grow with the iframe on mobile first paint).
    const HOST_RESET_CSS = '<style>html,body{height:auto;min-height:0;margin:0;}</style>';

    const cleanup = () => {
        rsObserver?.disconnect();
        rsObserver = undefined;
        msObserver?.disconnect();
        msObserver = undefined;
    };

    onDestroy(cleanup);

    $: if (iframeContainer && !rsObserver) {
        requestAnimationFrame(() => {
            // Component may have been destroyed between scheduling and firing —
            // Svelte nulls bind:this refs on unmount.
            if (!iframeContainer) return;

            const { contentWindow: window } = iframeContainer;

            if (!window) return;

            const headElem = window.document.getElementsByTagName('head')[0];
            const bodyElem = window.document.getElementsByTagName('body')[0];

            const rawHeadCode = unescape(head);
            const rawBodyCode = unescape(body);

            cleanup();

            if (provider !== 'provenexpert') {
                bodyElem.style.display = 'flex';
                bodyElem.style.justifyContent = 'center';
                bodyElem.style.alignItems = 'flex-start';
            }

            const headNode = window.document
                .createRange()
                .createContextualFragment(
                    `${NORMALIZE_CSS}${HOST_RESET_CSS}<style>body > a {display:inline-block;}body > .trustpilot-widget{width:100%;}</style>${rawHeadCode}`,
                );

            headElem.innerHTML = '';
            bodyElem.innerHTML = '';
            headElem.appendChild(headNode);

            if (provider === 'google-maps') {
                const extractIframeSrc = (iframeString: string) => {
                    const urlPattern = new RegExp('<iframe src="?(https[^"]+)"?');
                    const match = iframeString.match(urlPattern);
                    return match ? match[1] : null;
                };
                const iframeSrc = extractIframeSrc(rawBodyCode);

                if (!iframeSrc) {
                    console.warn(`Google Maps Embed Error`, {
                        data: { error: JSON.stringify(body) },
                    });
                    return;
                }

                iframeContainer.width = '100%';
                iframeContainer.style.border = '0';
                iframeContainer.style.aspectRatio = '4/3';
                iframeContainer.loading = 'lazy';
                iframeContainer.referrerPolicy = 'no-referrer-when-downgrade';
                iframeContainer.src = iframeSrc;
                return;
            }

            // Inject into a controlled root so we observe the correct element
            // even if the embed rewraps its own DOM later.
            const root = window.document.createElement('div');
            root.style.width = '100%';
            root.appendChild(window.document.createRange().createContextualFragment(rawBodyCode));
            bodyElem.appendChild(root);

            const measure = () => {
                if (!iframeContainer) return;
                // Measure the document, not just our wrapper: child margins can collapse
                // through `root` and leave it shorter than the actual rendered document.
                const docEl = window.document.documentElement;
                let height = Math.max(docEl.scrollHeight, bodyElem.scrollHeight);

                if (provider === 'provenexpert') height += 22; // prevents scrollbar in embed
                iframeContainer.style.height = `${Math.ceil(height)}px`;
            };

            rsObserver = new ResizeObserver(measure);
            rsObserver.observe(root);

            msObserver = new MutationObserver(measure);
            msObserver.observe(root, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
            });

            measure();

            // Late reflow source — fonts loading after initial paint can change height.
            window.document.fonts?.ready?.then(measure);
        });
    }

    const embedBorderRadiusClass = getBorderRadius('embed');
</script>

<div class="fun-flex fun-justify-center">
    <iframe
        bind:this={iframeContainer}
        class={cn('fun-w-full fun-transition-all max-sm:fun-rounded-none', {
            [embedBorderRadiusClass]: provider !== 'provenexpert',
        })}
        title={`${provider}-embed`}
    />
</div>
